import { useSnapshot } from 'valtio';
import { Vector3 } from 'three';
import { calculateThreeZoom, spatialRefState } from '@core/logic';
import { useMemo } from 'react';
import { readSearchParam } from '@core/logic/helper/searchParams';
import proj4 from 'proj4';

// Value based on empirical testing
const INITIAL_Z = 100;

export type MapboxPosition = {
  vector: Vector3;
  zoom: number;
  raw: string;
};

export const parseMapboxViewport = (
  mapboxViewport: string,
  lngLatToUTM: proj4.Converter,
): Omit<MapboxPosition, 'raw'> | undefined => {
  const parts = mapboxViewport
    .split('/')
    .filter((p) => p.length > 0)
    .map(Number);

  const [zoom, lat, lng] = parts;

  if (Number.isNaN(zoom) || Number.isNaN(lat) || Number.isNaN(lng)) {
    return;
  }

  const [x, y] = lngLatToUTM.forward([lng, lat]);

  return {
    vector: new Vector3(x, y, INITIAL_Z),
    zoom: calculateThreeZoom(zoom),
  };
};

export const useMapboxPosition = (): MapboxPosition | undefined => {
  // can't use router's useSearchParams inside Three Canvas
  const mapboxValue = readSearchParam('mapbox');
  const { lngLatToUTM } = useSnapshot(spatialRefState);

  const position = useMemo(() => {
    if (!mapboxValue || !lngLatToUTM) return;
    const parsed = parseMapboxViewport(mapboxValue, lngLatToUTM);
    if (!parsed) return;
    return { ...parsed, raw: mapboxValue };
  }, [mapboxValue, lngLatToUTM]);

  return position;
};
